import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { CUSTOMER_SEARCH_PAGE_NORMALIZER } from '@spartacus/asm/core'
import { OccAsmAdapter } from '@spartacus/asm/occ'
import { AsmConfig, CustomerSearchOptions, CustomerSearchPage } from '@spartacus/asm/root'
import {
  BaseSiteService,
  ConverterService,
  InterceptorUtil,
  OccEndpointsService,
  USE_CUSTOMER_SUPPORT_AGENT_TOKEN,
  WindowRef,
  tryNormalizeHttpError
} from '@spartacus/core'
import { Observable } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { EbLocalStorageKey } from '../../../configuration/models/eb-local-storage.model'

export interface EbAsmTokenResponse {
  access_token?: string
  accessToken?: string
  token_type?: string
  expires_in?: number
  token?: EbAsmTokenResponse
}

@Injectable({
  providedIn: 'root'
})
export class EbAsmAdapter extends OccAsmAdapter {
  protected ebActiveBaseSite: string
  constructor(
    protected http: HttpClient,
    protected occEndpointsService: OccEndpointsService,
    protected converterService: ConverterService,
    protected config: AsmConfig,
    protected baseSiteService: BaseSiteService,
    protected winRef: WindowRef
  ) {
    super(http, occEndpointsService, converterService, config, baseSiteService)
    this.baseSiteService.getActive().subscribe(value => (this.ebActiveBaseSite = value))
  }

  loadAsmClientSession(userId: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    })
    return this.http.post(
      this.occEndpointsService.buildUrl('loadAsmClientSession'),
      JSON.stringify(userId),
      {
        headers
      }
    )
  }

  setAgentSession(agentId: string): Observable<EbAsmTokenResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    })
    return this.http.post<EbAsmTokenResponse>(
      this.occEndpointsService.buildUrl('setAgentSession'),
      JSON.stringify(agentId),
      {
        headers
      }
    )
  }

  logoutAsmAgent(): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    })
    return this.http.post(
      this.occEndpointsService.buildUrl('logoutAsmAgent'),
      false,
      { headers, withCredentials: true }
    )
  }

  logoutAsmCustomer(): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    })
    return this.http.post(this.occEndpointsService.buildUrl('asmCustomerLogout'), null, {
      headers,
      withCredentials: true
    })
  }
  customerSearch(options: CustomerSearchOptions): Observable<CustomerSearchPage> {
    const headers = this.getAsmHeaders()
    let query: string
    let orderId: string
    let customerListId: string
    let params: HttpParams = new HttpParams().set('baseSite', this.ebActiveBaseSite)

    if (options.sort !== undefined) {
      params = params.set('sort', options.sort)
    } else {
      if (!options.customerListId) {
        params = params.set('sort', 'byNameAsc')
      }
    }

    if (options.query !== undefined) {
      query = options.query
    }
    if (options.orderId !== undefined) {
      orderId = options.orderId
    }

    if (options.pageSize !== undefined) {
      params = params.set('pageSize', options.pageSize.toString())
    }

    if (options.currentPage !== undefined) {
      params = params.set('page', options.currentPage.toString())
    }

    if (options.customerListId !== undefined) {
      customerListId = options.customerListId
    }

    const url = this.occEndpointsService.buildUrl(
      'asmCustomerSearch',
      {},
      {
        baseSite: false,
        prefix: false
      }
    )

    return this.http
      .post<CustomerSearchPage>(url, { query, orderId, customerListId }, { headers, params })
      .pipe(
        catchError(error => {
          throw tryNormalizeHttpError(error, this.logger)
        }),
        this.converterService.pipeable(CUSTOMER_SEARCH_PAGE_NORMALIZER)
      )
  }

  private getAsmHeaders(): HttpHeaders {
    const accessToken = this.getAsmAccessToken()
    const headers = InterceptorUtil.createHeader(
      USE_CUSTOMER_SUPPORT_AGENT_TOKEN,
      true,
      new HttpHeaders()
    )

    return accessToken ? headers.set('Authorization', `Bearer ${accessToken}`) : headers
  }

  private getAsmAccessToken(): string | undefined {
    try {
      const token = JSON.parse(
        this.winRef.localStorage?.getItem(EbLocalStorageKey.ASM_KEY)
      ) as EbAsmTokenResponse

      return token?.access_token?.trim() || undefined
    } catch {
      return undefined
    }
  }
}
